// The loading indicator (#ind_container), the owner's pick of 2026-10-16 (prototyped with five other
// looks): a frosted-glass card hanging from the top edge, exactly as wide as the See Rank / Login pair
// under it (at least 200 px), with a ring of the four suits turning and cards dealt from a deck onto five
// places, left to right. It slides down while an action the player waits for runs (ui/busy.ts) and back
// up when it ends, then gets `hidden` (the tests read display: none as "off"; legacy indicator.js slid
// in at top 0 and out above the screen the same way).
import { useEffect, useLayoutEffect, useState, type CSSProperties } from 'react';
import { t } from '../i18n';
import { backSrc, backSrcSet, faceSrc, faceSrcSet } from '../options/assets';
import { useUiState } from '../store/uiStore';

const SLIDE_MS = 200; // the CSS slide (app.css, .indicator transition)
const DEALT = ['As', 'Ah', 'Ad', 'Ac', 'Kh'];
const MIN_W = 200;

/** The indicator's width and centre: always the page centre (owner 2026-10-18: "powinien być zawsze" centred;
 *  it followed the See Rank / Login pair, which sits off centre in a narrow window), as wide as that pair (at
 *  least 200 px, at most the page less 8 px a side). The page width is the layout width (`clientWidth`). */
function pairBox(): { w: number; x: number } {
  const vw = document.documentElement.clientWidth;
  const a = document.getElementById('game_action_globalstats')?.getBoundingClientRect();
  const b = document.getElementById('game_action_auth')?.getBoundingClientRect();
  const pair = a && b && a.width && b.width ? Math.max(a.right, b.right) - Math.min(a.left, b.left) : 0;
  const w = Math.min(Math.max(pair, MIN_W), vw - 16);
  return { w: Math.round(w), x: Math.round(vw / 2) };
}

export function Indicator() {
  const ui = useUiState();
  const loading = ui.loading;
  const { deckType, cardBackType } = ui.options.skin;
  // `mounted`: not display: none (set a tick after loading starts, so the slide-in runs; cleared when the
  // slide-out is over). The slide itself follows `loading` directly, so the indicator starts to leave in
  // the same commit as the action ends (BOOT-005: before the first deal).
  const [mounted, setMounted] = useState(loading);
  const [pair, setPair] = useState<{ w: number; x: number } | null>(null);

  useEffect(() => {
    const id = window.setTimeout(() => setMounted(loading), loading ? 0 : SLIDE_MS);
    return () => clearTimeout(id);
  }, [loading]);

  // Measure the pair each time the indicator comes up, BEFORE it slides in (a layout effect runs before
  // the paint), and on every resize while it is up. The first measure (the start-up) can be stale by the
  // next show: the labels change size when the language loads, so a later first show slid in ~15 px off
  // and then jumped (owner 2026-10-16) when it was measured only one frame after the slide had started.
  const up = loading || mounted;
  useLayoutEffect(() => {
    if (!up) return;
    const fit = () => setPair(pairBox());
    fit();
    addEventListener('resize', fit);
    return () => removeEventListener('resize', fit);
  }, [up]);

  const style = pair ? ({ '--ind-w': `${pair.w}px`, '--ind-x': `${pair.x}px` } as CSSProperties) : undefined;
  return (
    <div
      id="ind_container"
      className="indicator"
      data-on={loading && mounted ? '' : undefined}
      hidden={(!mounted && !loading) || undefined}
      role="status"
      aria-label={t('game.loading')}
      style={style}
    >
      <span className="indicator__spin" aria-hidden="true">
        <span className="indicator__suit indicator__suit--0">♠</span>
        <span className="indicator__suit indicator__suit--1 indicator__suit--red">♥</span>
        <span className="indicator__suit indicator__suit--2">♣</span>
        <span className="indicator__suit indicator__suit--3 indicator__suit--red">♦</span>
      </span>
      <span className="indicator__stage" aria-hidden="true">
        <img className="indicator__deck" src={backSrc(cardBackType)} srcSet={backSrcSet(cardBackType)} alt="" />
        {DEALT.map((id, i) => (
          <img
            key={id}
            className="indicator__card"
            src={faceSrc(deckType, id)}
            srcSet={faceSrcSet(deckType, id)}
            alt=""
            style={{ '--to': `${(i + 1) * 23 + 6}px`, animationDelay: `${i * 0.32}s` } as CSSProperties}
          />
        ))}
      </span>
    </div>
  );
}
